import {EditorView} from '@codemirror/view';
import {tokens} from '@packages/utilities/styled';
import {merge} from 'lodash';
import type {EditorConfig} from '../editor/editor';

type StyleSpec = {
  [propOrSelector: string]: string | number | StyleSpec | null;
};

export interface ThemeConfig {
  theme?: EditorConfig['theme'];
  height?: number | string;
  fontFamily?: string;
  singleLine?: boolean;
  borderRadius?: number | string;
}

export function createTheme({
  theme = 'light',
  height,
  fontFamily,
  singleLine,
  borderRadius,
}: ThemeConfig) {
  const dark = theme.startsWith('dark');

  const defaultTheme: Record<string, StyleSpec> = {
    '&': {
      backgroundColor: tokens.colors.uiInputBackground!,
      borderRadius:
        borderRadius !== undefined
          ? `${borderRadius}px`
          : `${tokens.radii[2]}px`, // Prevent editor from clipping the border radius of the wrapper
      boxShadow: tokens.boxShadows.inputInner,
      fontSize: tokens.typography.fontSize.body,
      resize: 'none', // Prevent draggable resize handle — fixed height
    },
    '&:hover': {
      borderColor: tokens.colors.uiInputBorderColorHover!,
    },
    '&.cm-editor.cm-focused': {
      borderColor: tokens.colors.blueBorder,
      outline: 'none',
    },
    '& .cm-scroller': {
      overflow: singleLine ? 'hidden' : 'auto', // Expand editor content to fill the wrapper
      resize: 'none', // Prevent draggable resize handle — fixed height
    },
    '& .cm-scroller::-webkit-scrollbar': {
      width: '9px',
      height: '9px',
    },
    '& .cm-scroller::-webkit-scrollbar-track, & .cm-scroller::-webkit-scrollbar-corner':
      {
        background: 'transparent',
      },
    '& .cm-scroller::-webkit-scrollbar-thumb': {
      backgroundColor: tokens.colors.uiScrollbarThumb!,
      borderRadius: `${tokens.radii[5]}px`,
      border: '2px solid rgba(0,0,0,0)',
      backgroundClip: 'padding-box',
      '&:hover': {
        backgroundColor: tokens.colors.uiScrollbarThumbHover!,
      },
    },
    '& .cm-content': {
      fontFamily: fontFamily || tokens.typography.fontFamily.code,
      color: tokens.colors.textPrimary,
      lineHeight: tokens.typography.lineHeight.body,
      cursor: 'text',
    },
    '& .cm-content[aria-readonly="true"]': {
      color: tokens.colors.textDimmed,
      cursor: 'not-allowed',
    },
    '& .cm-gutters': {
      backgroundColor: tokens.colors.background1,
      borderRight: `1px solid ${tokens.colors.border1}`,
      color: tokens.colors.textDimmed,
    },
    '& .cm-gutter.cm-lineNumbers': {
      fontSize: tokens.typography.fontSize.body,
    },
    '& .cm-activeLineGutter': {
      color: tokens.colors.blueText,
      backgroundColor: 'unset',
    },
    '& .cm-activeLine': {
      backgroundColor: 'rgba(0,0,0,0.05)', // Needs to be have opacity to allow the selection to show
    },
    '&.cm-editor.cm-focused > .cm-scroller > .cm-selectionLayer .cm-selectionBackground':
      {
        background: tokens.colors.blueTransparentHover, // Focused editor selection
      },
    '&.cm-editor .cm-selectionBackground, .cm-content ::selection': {
      background: tokens.colors.blueTransparent, // Unfocused editor selection
    },
    /* Force selected text to textPrimary so syntax-highlighted tokens stay
     * readable through the translucent selection background.
     */
    '& .cm-content ::selection': {
      color: tokens.colors.textPrimary,
    },
    '& .cm-placeholder': {
      color: tokens.colors.textDimmed,
    },
    '& .cm-line .w-invalid': {
      backgroundColor: tokens.colors.redTransparentHover,
      borderRadius: `${tokens.radii[2]}px`,
      padding: tokens.space[0.5],
    },
    // Search match highlights — VS Code style
    '& .cm-searchMatch': {
      backgroundColor: dark ? '#515C6A !important' : '#E8C56B !important',
      borderRadius: '2px',
    },
    '& .cm-searchMatch.cm-searchMatch-selected': {
      backgroundColor: dark ? '#1A5A2E !important' : '#A4D6FF !important',
    },
    '& .cm-selectionMatch': {
      backgroundColor: dark ? '#ADD6FF26 !important' : '#ADD6FF80 !important',
    },
    // Search panel container — reset CM6 default borders to prevent double lines
    '& .cm-panels': {
      backgroundColor: `${tokens.colors.background1} !important`,
      color: `${tokens.colors.textPrimary} !important`,
      border: 'none !important',
    },
    '& .cm-panels-top': {
      borderBottom: 'none !important',
    },
    '& .cm-panel.cm-search': {
      backgroundColor: `${tokens.colors.background1} !important`,
      padding: '8px !important',
      borderBottom: `1px solid ${tokens.colors.border1} !important`,
      lineHeight: '24px !important',
    },
    // Search inputs — use .cm-textfield (CodeMirror's actual class for search inputs)
    '& .cm-textfield': {
      backgroundColor: `${tokens.colors.uiInputBackground} !important`,
      color: `${tokens.colors.textPrimary} !important`,
      border: `1px solid ${tokens.colors.border2} !important`,
      borderRadius: `${tokens.radii[2]}px !important`,
      padding: '4px !important',
      height: '24px !important',
      boxSizing: 'border-box !important',
      fontSize: '11.5px !important',
      fontFamily:
        'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", Helvetica, Arial, sans-serif !important',
      lineHeight: '1rem !important',
      boxShadow: `${tokens.boxShadows.inputInner} !important`,
      outline: `1px solid ${tokens.colors.uiNumberInputValueWrapperOutline} !important`,
      outlineOffset: '-1px !important',
      verticalAlign: 'middle !important',
      colorScheme: dark ? 'dark !important' : 'light !important',
    },
    // Hide the browser's datalist dropdown caret on inputs with a list attribute
    '& .cm-textfield::-webkit-calendar-picker-indicator': {
      display: 'none !important',
    },
    '& .cm-textfield::-webkit-list-button': {
      display: 'none !important',
    },
    '& .cm-textfield:focus': {
      outline: 'none !important',
      borderColor: `${tokens.colors.blueBorder} !important`,
    },
    // Search buttons — outline variant (transparent bg + inset border) for both themes
    '& .cm-button': {
      background: 'transparent !important',
      color: `${tokens.colors.actionSecondaryText} !important`,
      border: 'none !important',
      borderRadius: `${tokens.radii[2]}px !important`,
      boxShadow: `0 0 0 1px ${tokens.colors.actionSecondaryBorder} inset !important`,
      padding: '0 10px !important',
      lineHeight: '24px !important',
      fontSize: '11.5px !important',
      fontFamily: 'Inter, Helvetica, Arial, sans-serif !important',
      cursor: 'pointer !important',
      marginLeft: '0px !important',
      fontWeight: '400 !important',
      whiteSpace: 'nowrap !important',
      textAlign: 'center !important',
      outline: 'none !important',
      textTransform: 'capitalize !important',
      verticalAlign: 'middle !important',
    },
    '& .cm-button:hover': {
      background: 'transparent !important',
      boxShadow: `0 0 0 1px ${tokens.colors.actionSecondaryBorderHover} inset !important`,
    },
    '& .cm-button:active': {
      background: 'transparent !important',
    },
    // Custom search panel layout
    '& .cm-wf-search': {
      display: 'flex !important',
      flexDirection: 'column !important',
      gap: '4px !important',
    },
    '& .cm-wf-search-row': {
      display: 'flex !important',
      alignItems: 'center !important',
      gap: '0px !important',
    },
    '& .cm-wf-replace-row': {
      marginTop: '0 !important',
    },
    '& .cm-wf-toggle-spacer': {
      visibility: 'hidden !important',
      pointerEvents: 'none !important',
    },
    '& .cm-wf-hidden': {
      display: 'none !important',
    },
    // Push close button to the far right and ensure it stays in flex flow
    '& .cm-wf-search button[name="close"]': {
      marginLeft: 'auto !important',
      position: 'static !important',
    },
    // Text fields inside search rows should have a fixed width so Find and Replace align
    '& .cm-wf-search-row > .cm-textfield': {
      width: '150px !important',
      flexShrink: '0 !important',
    },
    // Icon button — shared base
    '& .cm-wf-icon-btn': {
      display: 'flex !important',
      alignItems: 'center !important',
      justifyContent: 'center !important',
      width: '24px !important',
      height: '24px !important',
      padding: '0 !important',
      cursor: 'pointer !important',
      flexShrink: '0 !important',
      color: `${tokens.colors.textSecondary} !important`,
      borderRadius: `${tokens.radii[2]}px !important`,
      verticalAlign: 'middle !important',
      boxSizing: 'border-box !important',
    },
    // Ghost variant — transparent, no border/shadow (used by toggle chevron)
    '& .cm-wf-icon-btn.cm-wf-ghost': {
      background: 'transparent !important',
      border: 'none !important',
      boxShadow: 'none !important',
    },
    '& .cm-wf-icon-btn.cm-wf-ghost:hover': {
      backgroundColor: `${tokens.colors.uiBackgroundHover} !important`,
    },
    // Outline variant — transparent bg + inset border for both themes
    '& .cm-wf-icon-btn.cm-wf-outline': {
      background: 'transparent !important',
      border: 'none !important',
      boxShadow: `0 0 0 1px ${tokens.colors.actionSecondaryBorder} inset !important`,
    },
    '& .cm-wf-icon-btn.cm-wf-outline:hover': {
      background: 'transparent !important',
      boxShadow: `0 0 0 1px ${tokens.colors.actionSecondaryBorderHover} inset !important`,
    },
    // Disabled state for buttons
    '& .cm-button:disabled, & .cm-wf-icon-btn:disabled': {
      opacity: '0.4 !important',
      cursor: 'not-allowed !important',
    },
    // Match count inside the All button
    '& .cm-wf-match-count': {
      opacity: '0.7',
      fontSize: '11px !important',
      textTransform: 'none !important',
    },
    '& .cm-wf-match-count:empty': {
      display: 'none !important',
    },
    // Labels — match "Line wrapping" checkbox label style (sentence case)
    '& .cm-panel.cm-search label': {
      color: `${tokens.colors.textSecondary} !important`,
      fontSize: '11.5px !important',
      fontFamily: 'Inter, Helvetica, Arial, sans-serif !important',
      marginLeft: '4px !important',
      display: 'inline-block !important',
      cursor: 'pointer !important',
      userSelect: 'none !important',
      verticalAlign: 'middle !important',
      lineHeight: '24px !important',
    },
    // Checkboxes — match designer Checkbox component
    '& .cm-panel.cm-search input[type="checkbox"]': {
      appearance: 'none !important' as any,
      width: '12px !important',
      height: '12px !important',
      border: `1px solid ${tokens.colors.uiCheckboxRadioBorderColor} !important`,
      borderRadius: '2px !important',
      backgroundColor: 'transparent !important',
      cursor: 'pointer !important',
      marginRight: '4px !important',
      position: 'relative !important' as any,
      verticalAlign: 'middle !important',
      flexShrink: '0 !important',
      backgroundSize: '10px 8px !important',
      backgroundPosition: 'center !important',
      backgroundRepeat: 'no-repeat !important',
    },
    '& .cm-panel.cm-search input[type="checkbox"]:checked': {
      backgroundColor: `${tokens.colors.actionPrimaryBackground} !important`,
      borderColor: `${tokens.colors.actionPrimaryBackground} !important`,
      backgroundImage:
        "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 13 11'%3E%3Cpath fill='none' stroke='white' stroke-width='2' stroke-miterlimit='10' d='M11 2L5.5 8.5 2 5'/%3E%3C/svg%3E\") !important",
    },
  };

  if (height) {
    merge(defaultTheme, {
      '&': {
        height: typeof height === 'number' ? `${height}px` : height,
      },
    });
  }

  if (singleLine) {
    merge(defaultTheme, {
      '& .cm-content': {
        padding: '2px 0',
        display: 'flex',
        alignItems: 'center',
      },
    });
  }

  return EditorView.theme(defaultTheme, {dark});
}
